/**
 * Safe markdown → HTML for platform announcements (shared by the admin authoring
 * preview and the public render). Two pure, dependency-free functions:
 *
 *  - `renderAnnouncementHtml(md)` — markdown → SANITIZED html. ESCAPE-FIRST: every
 *    HTML metacharacter in the source is escaped BEFORE any markdown transform, so
 *    raw HTML (`<script>`, `<iframe>`, `<img onerror>`, inline `style=`, `onclick=`)
 *    in the source can NEVER reach the output — it becomes inert text. The renderer
 *    then emits ONLY the allowlisted tags from markdown syntax:
 *        h1 h2 h3 p strong em ul ol li blockquote code pre a
 *    There is no raw-HTML passthrough, so the renderer IS the allowlist (no separate
 *    sanitizer library needed). `<a>` hrefs are restricted to http/https/mailto.
 *
 *  - `autodetectHeadings(text)` — paste helper. Keeps existing `#`/`##`/`###` and
 *    promotes a short, single-line, blank-line-isolated, punctuation-free line to a
 *    markdown `## ` heading (e.g. a pasted "Security" line) so headings survive a
 *    plain-text copy-paste.
 *
 * Backward compatible: an existing plain-text body is valid markdown and renders as
 * `<p>` paragraphs (line breaks preserved per blank-line rule).
 */

const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'p', 'strong', 'em', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a',
] as const;

/** The allowlist, exported for tests/docs. The renderer never emits anything else. */
export const ANNOUNCEMENT_ALLOWED_TAGS: readonly string[] = ALLOWED_TAGS;

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

/** Only http(s)/mailto links are emitted; anything else (javascript:, data:, …) is dropped. */
function safeHref(href: string): string | null {
  const h = href.trim();
  return /^(https?:|mailto:)/i.test(h) ? h : null;
}

/**
 * Inline markdown on ALREADY-ESCAPED text. Emits only strong/em/code/a. Because the
 * input is escaped, no markdown token can introduce a raw tag.
 */
function renderInline(escaped: string): string {
  let s = escaped;
  // Inline code first so * / _ inside backticks are not treated as emphasis.
  s = s.replace(/`([^`]+)`/g, (_m, c: string) => `<code>${c}</code>`);
  // Links [text](href) — validate scheme; on reject, keep the link text only.
  s = s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (_m, text: string, href: string) => {
    const safe = safeHref(href);
    return safe
      ? `<a href="${safe}" target="_blank" rel="noopener noreferrer nofollow">${text}</a>`
      : text;
  });
  // Bold then italic (bold first so ** is not consumed by the single-* rule).
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__([^_\n]+)__/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/(^|[^_\w])_([^_\n]+)_/g, '$1<em>$2</em>');
  return s;
}

function splitLines(src: string): string[] {
  return src.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
}

export function renderAnnouncementHtml(markdown: string): string {
  if (!markdown) return '';
  const lines = splitLines(markdown);
  const out: string[] = [];
  let para: string[] = [];

  const flushPara = (): void => {
    if (para.length) {
      out.push(`<p>${renderInline(escapeHtml(para.join(' ')))}</p>`);
      para = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = (lines[i] ?? '').trim();

    // Heading: #, ##, ### (1-3).
    const h = /^(#{1,3})\s+(.+?)\s*#*$/.exec(line);
    if (h) {
      flushPara();
      const level = (h[1] ?? '#').length;
      out.push(`<h${level}>${renderInline(escapeHtml(h[2] ?? ''))}</h${level}>`);
      i++;
      continue;
    }

    // Fenced code block ``` … ``` (raw, escaped, never parsed for inline).
    if (/^```/.test(line)) {
      flushPara();
      i++;
      const buf: string[] = [];
      while (i < lines.length && !/^```/.test((lines[i] ?? '').trim())) {
        buf.push(lines[i] ?? '');
        i++;
      }
      i++; // consume closing fence (if present)
      out.push(`<pre><code>${escapeHtml(buf.join('\n'))}</code></pre>`);
      continue;
    }

    // Blockquote (one or more leading-> lines).
    if (/^>\s?/.test(line)) {
      flushPara();
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test((lines[i] ?? '').trim())) {
        buf.push((lines[i] ?? '').trim().replace(/^>\s?/, ''));
        i++;
      }
      out.push(`<blockquote>${renderInline(escapeHtml(buf.join(' ')))}</blockquote>`);
      continue;
    }

    // Unordered list (-, *, +).
    if (/^[-*+]\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^[-*+]\s+/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^[-*+]\s+/, ''));
        i++;
      }
      out.push(`<ul>${items.map((x) => `<li>${renderInline(escapeHtml(x))}</li>`).join('')}</ul>`);
      continue;
    }

    // Ordered list (1. / 1)).
    if (/^\d+[.)]\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^\d+[.)]\s+/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^\d+[.)]\s+/, ''));
        i++;
      }
      out.push(`<ol>${items.map((x) => `<li>${renderInline(escapeHtml(x))}</li>`).join('')}</ol>`);
      continue;
    }

    // Blank line → paragraph boundary.
    if (line === '') {
      flushPara();
      i++;
      continue;
    }

    // Otherwise: accumulate into the current paragraph.
    para.push(line);
    i++;
  }
  flushPara();
  return out.join('\n');
}

/**
 * Promote plain-text headings to markdown on paste. A line becomes `## <line>` when:
 *  - it is non-empty, ≤ 64 chars, ≤ 8 words,
 *  - isolated by blank lines (or doc start/end) above and below,
 *  - not already a markdown block (#, list, quote, fence),
 *  - has no trailing sentence punctuation (. : ; , ! ?),
 *  - is not a bare URL.
 * Existing `#`/`##`/`###` lines are kept verbatim.
 */
export function autodetectHeadings(text: string): string {
  if (!text) return text;
  const lines = splitLines(text);
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();
    const prevBlank = i === 0 || (lines[i - 1] ?? '').trim() === '';
    const nextBlank = i === lines.length - 1 || (lines[i + 1] ?? '').trim() === '';
    const isMarkdownBlock = /^(#{1,3}\s|[-*+]\s|\d+[.)]\s|>|```)/.test(trimmed);
    const looksLikeHeading =
      trimmed.length > 0 &&
      trimmed.length <= 64 &&
      trimmed.split(/\s+/).length <= 8 &&
      prevBlank &&
      nextBlank &&
      !isMarkdownBlock &&
      !/[.:;,!?]$/.test(trimmed) &&
      !/^https?:/i.test(trimmed);
    out.push(looksLikeHeading ? `## ${trimmed}` : line);
  }
  return out.join('\n');
}
